/**
 * Base Chart Component
 * Wrapper component with common functionality for all charts
 */

import React, { ReactNode } from 'react'
import { ChartSkeleton } from './ChartSkeleton'
import { ChartError } from './ChartError'

interface BaseChartProps {
  children: ReactNode
  loading?: boolean
  error?: Error | null
  onRetry?: () => void
  width?: number | string
  height?: number
  ariaLabel?: string
}

class ChartErrorBoundary extends React.Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Chart rendering error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }

    return this.props.children
  }
}

export const BaseChart: React.FC<BaseChartProps> = ({
  children,
  loading = false,
  error = null,
  onRetry,
  width = '100%',
  height = 400,
  ariaLabel
}) => {
  if (loading) {
    return <ChartSkeleton width={width} height={height} />
  }

  if (error) {
    return (
      <ChartError
        message={error.message}
        onRetry={onRetry}
        width={width}
        height={height}
      />
    )
  }

  return (
    <ChartErrorBoundary
      fallback={
        <ChartError
          message="Erro ao renderizar gráfico"
          onRetry={onRetry}
          width={width}
          height={height}
        />
      }
    >
      <div
        role="img"
        aria-label={ariaLabel}
        className="[&_.recharts-wrapper]:text-xs [&_.recharts-wrapper]:font-sans"
        style={{ width, height }}
      >
        {children}
      </div>
    </ChartErrorBoundary>
  )
}
